import type { JointAngles, JointKey } from './JointAngles';
import type { PoseRecord } from './PoseRecordStore';

export interface PoseExportMeta {
  engineId?: string;
  engineLabel?: string;
  source?: string;
  resolution?: { width: number; height: number };
}

const ANGLE_KEYS: readonly JointKey[] = [
  'leftElbow',
  'rightElbow',
  'leftShoulder',
  'rightShoulder',
  'leftHip',
  'rightHip',
  'leftKnee',
  'rightKnee',
  'leftAnkle',
  'rightAnkle',
  'leftWrist',
  'rightWrist',
];

export function posesToJson(records: readonly PoseRecord[], meta: PoseExportMeta = {}): string {
  return JSON.stringify(
    {
      exportedAt: new Date().toISOString(),
      appMode: 'pose',
      engineId: meta.engineId,
      engineLabel: meta.engineLabel,
      source: meta.source,
      resolution: meta.resolution,
      frames: records.map((r) => ({
        t: r.t,
        angles: r.angles,
        anglesPerPerson: r.anglesPerPerson,
        poses: r.poses.map((p) => ({
          fps: p.fps,
          inferenceTimeMs: p.inferenceTimeMs,
          nodes: p.nodes.map((n) => ({
            id: n.id,
            name: n.name,
            x: n.x,
            y: n.y,
            score: n.score,
          })),
        })),
      })),
    },
    null,
    2,
  );
}

export function nodesToCsv(records: readonly PoseRecord[]): string {
  const rows: string[] = ['t_ms,person,node_id,node_name,x,y,score'];
  for (const r of records) {
    for (let p = 0; p < r.poses.length; p += 1) {
      const pose = r.poses[p];
      for (const n of pose.nodes) {
        rows.push(
          `${r.t.toFixed(3)},${p},${n.id},${n.name},${n.x.toFixed(5)},${n.y.toFixed(5)},${n.score.toFixed(3)}`,
        );
      }
    }
  }
  return rows.join('\n');
}

export function anglesToCsv(records: readonly PoseRecord[]): string {
  const rows: string[] = ['t_ms,person,joint,angle_deg'];
  for (const r of records) {
    const perPerson = r.anglesPerPerson;
    if (perPerson && perPerson.length > 0) {
      for (let p = 0; p < perPerson.length; p += 1) {
        const a = perPerson[p];
        if (!a) continue;
        for (const key of ANGLE_KEYS) {
          const v = a[key];
          if (typeof v !== 'number' || !Number.isFinite(v)) continue;
          rows.push(`${r.t.toFixed(3)},${p},${key},${v.toFixed(2)}`);
        }
      }
      continue;
    }
    const a: JointAngles | undefined = r.angles;
    if (!a) continue;
    for (const key of ANGLE_KEYS) {
      const v = a[key];
      if (typeof v !== 'number' || !Number.isFinite(v)) continue;
      rows.push(`${r.t.toFixed(3)},0,${key},${v.toFixed(2)}`);
    }
  }
  return rows.join('\n');
}
